import { Link } from '@buzola/router'
import {
	BarChart3Icon,
	BoxIcon,
	BuildingIcon,
	ChevronsUpDown,
	FileBoxIcon,
	KeyIcon,
	LayersIcon,
	LogOutIcon,
	MonitorIcon,
	SettingsIcon,
	UsersIcon,
} from 'lucide-react'
import { Menu, MenuItem, MenuSection } from '~/lib/ui/menu'
import { Avatar, AvatarFallback } from '~/lib/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '~/lib/ui/dropdown'
import { SidebarMenuButton } from '~/lib/ui/sidebar'
import { useIsMobile } from '~/lib/utils/use-mobile'
import { LogoutTrigger } from '@contember/react-identity'
import { getLastOrgId } from '~/lib/routing/org-context'

// Super-admins enter an org with the `admin` role; org admins with `orgAdmin`.
// Read-only viewers (`orgViewer`) have neither, so org-management UI is hidden.
const canManageOrg = (roles: Set<string>) => roles.has('admin') || roles.has('orgAdmin')

export const OrgNavigation = () => (
	<Menu>
		<MenuSection label="Workspace">
			<MenuItem label="Instances" to="app/org/instances" icon={<MonitorIcon />} />
			<MenuItem label="Agent Bundles" to="app/org/bundles" icon={<BoxIcon />} />
			<MenuItem label="Resources" to="app/org/resources" icon={<FileBoxIcon />} />
			<MenuItem label="Usage" to="app/org/usage" icon={<BarChart3Icon />} />
		</MenuSection>
		<MenuSection label="Configure">
			<MenuItem label="Members" to="app/org/members" icon={<UsersIcon />} role={canManageOrg} />
			<MenuItem label="Settings" to="app/org/settings" icon={<SettingsIcon />} role={canManageOrg} />
		</MenuSection>
		<MenuSection label="Elsewhere">
			<MenuItem label="Switch organization" to="app" icon={<BuildingIcon />} />
			<MenuItem label="Superadmin" to="app/admin/organizations" icon={<LayersIcon />} role="admin" />
		</MenuSection>
	</Menu>
)

export const AdminNavigation = () => {
	const lastOrgId = getLastOrgId()
	return (
		<Menu>
			<MenuSection label="Platform">
				<MenuItem label="Organizations" to="app/admin/organizations" icon={<BuildingIcon />} />
				<MenuItem label="Users" to="app/admin/users" icon={<UsersIcon />} />
				<MenuItem label="Sandbox Templates" to="app/admin/templates" icon={<LayersIcon />} />
				<MenuItem label="Session Stats" to="app/admin/session-stats" icon={<BarChart3Icon />} />
				<MenuItem label="API Keys" to="app/admin/api-keys" icon={<KeyIcon />} />
			</MenuSection>
			<MenuSection label="Elsewhere">
				{lastOrgId
					? <MenuItem label="Back to organization" to="app/org/instances" icon={<MonitorIcon />} />
					: <MenuItem label="Pick organization" to="app" icon={<MonitorIcon />} />}
			</MenuSection>
		</Menu>
	)
}

export const UserNavigation = () => {
	const isMobile = useIsMobile()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<SidebarMenuButton
					size="lg"
					className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
				>
					<Avatar className="icon h-7 w-7 rounded-md ring-1 ring-foreground/10">
						<AvatarFallback
							className="rounded-md text-background font-display italic text-base"
							style={{ backgroundColor: 'hsl(var(--foreground))' }}
						>r</AvatarFallback>
					</Avatar>
					<div className="grid flex-1 text-left leading-tight">
						<span className="truncate font-semibold text-[13px]">Account</span>
						<span className="truncate text-[10px] text-muted-foreground font-mono uppercase tracking-[0.14em]">roj · cloud</span>
					</div>
					<ChevronsUpDown className="ml-auto size-3.5 opacity-60" />
				</SidebarMenuButton>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
				side={isMobile ? 'bottom' : 'right'}
				align="end"
				sideOffset={4}
			>
				<DropdownMenuLabel className="p-0 font-normal">
					<div className="flex items-center gap-2.5 pl-3 pr-1 py-2 text-left text-sm">
						<Avatar className="h-8 w-8 rounded-md ring-1 ring-foreground/10">
							<AvatarFallback
								className="rounded-md text-background font-display italic text-lg"
								style={{ backgroundColor: 'hsl(var(--foreground))' }}
							>r</AvatarFallback>
						</Avatar>
						<div className="grid flex-1 text-left leading-tight">
							<span className="truncate font-semibold">Roj Cloud</span>
							<span className="truncate text-[11px] text-muted-foreground">Agent platform</span>
						</div>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem asChild>
						<Link to="app">
							<BuildingIcon size={16} className="mr-2" />
							Switch organization
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<LogoutTrigger>
							<button className="flex w-full items-center gap-2">
								<LogOutIcon size={16} /> Log out
							</button>
						</LogoutTrigger>
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
